import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { InventoryService } from '../../../service/api/inventory-service';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { saveAs } from 'Core/utils';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { selectStatusLoading } from '../../../core/redux/status/selectors';
const { INVENTORY_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'inventory';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
    type: null,
    serialNumber: '',
    classFilters: { FG: true, PL: false, IN: false, Others: false }
  },
  inventoryData: null,
  detailFilters:{
    searchText: ''
  },
  filterVisible: false,
  tileView: true,
  selectedInventory: null,
  editModalVisible: false,
};

export const fetchInventories = createAsyncThunk(
  'inventory/fetchInventories',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await InventoryService.getAllInventory(filters);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Inventories');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Inventories');
    }
  },
);

export const addInventory = createAsyncThunk(
  'inventory/addInventory',
  async (inventoryPayload, { rejectWithValue }) => {
    try {
      const response = await InventoryService.createInventory(inventoryPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add Inventories');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add Inventories');
    }
  },
);

export const editInventory = createAsyncThunk(
  'inventory/editInventory',
  async (inventoryPayload, { rejectWithValue }) => {
    try {
      const response = await InventoryService.createInventory(inventoryPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update inventory');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update inventory');
    }
  },
);

export const downloadInventories = createAsyncThunk(
  'inventory/downloadInventories',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await InventoryService.downloadInventory(filters);
      const blob = response;
      saveAs(blob, `Inventories.xlsx`);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to download Inventories');
    }
  },
);

export const deleteInventory = createAsyncThunk(
  'inventory/deleteInventory',
  async (inventoryId, { rejectWithValue }) => {
    try {
      const response = await InventoryService.deleteInventory(inventoryId);
      if (response.status === 200) {
        return inventoryId;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete inventory');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete inventory');
    }
  },
);

export const fetchInventoriesWithFilters = () => (dispatch, getState) => {
  const filters = getState().inventory.filters;
  dispatch(
    fetchInventories({
      searchText: filters.searchText,
      customer: filters?.customer?.value,
      status: filters?.status?.value,
      inventoryDate: filters?.shipDate,
      serialNumber: filters?.serialNumber,
      startDate: filters?.startDate,
      endDate: filters?.endDate,
      classFilters: filters.classFilters,
    }),
  );
};

export const downloadInventoriesWithFilters = () => (dispatch, getState) => {
  const filters = getState().inventory.filters;
  dispatch(
    downloadInventories({
      searchText: filters.searchText,
      customer: filters?.customer?.value,
      status: filters?.status?.value,
      inventoryDate: filters?.shipDate,
    }),
  );
};

export const fetchInventorySerialData = createAsyncThunk(
  'inventory/fetchInventorySerialData',
  async (inventory, { rejectWithValue }) => {
    try {
      const response = await InventoryService.getInventoryById(inventory.partId);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load shipment data');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load shipment data');
    }
  },
);

export const quarantineInventory = createAsyncThunk(
  'inventory/quarantineinv',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await InventoryService.Quarantine(payload);
      if (response.status === 200) {
        return payload;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to quarantine');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to quarantine');
    }
  },
);

export const releaseInventory = createAsyncThunk(
  'inventory/releaseinv',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await InventoryService.Release(payload);
      if (response.status === 200) {
        return payload;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to release inventory');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to release inventory');
    }
  },
);

export const { reducer, actions } = createSlice({
  name: 'inventory',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setDetailSearchText: (state, action) => {
      state.detailFilters.searchText = action.payload;
    },
    setCustomer: (state, action) => {
      state.filters.customer = action.payload;
    },
    setSerialNumber: (state, action) => {
      state.filters.serialNumber = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setStartDate: (state, action) => {
      state.filters.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.filters.endDate = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload; // Add reducer to update tileView
    },
    setClassFilters: (state, action) => {
      state.filters.classFilters = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchInventories.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInventories.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchInventories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addInventory.fulfilled, (state, action) => {
        const newInventory = {
          ...action.payload,
          isNewlyCreated: true,
        };
        state.data = [newInventory, ...state.data];
      })
      .addCase(addInventory.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editInventory.fulfilled, (state, action) => {
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editInventory.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchInventorySerialData.pending, state => {
        state.inventoryLoading = true;
        state.inventoryError = null;
      })
      .addCase(fetchInventorySerialData.fulfilled, (state, action) => {
        state.inventoryLoading = false;
        state.inventoryData = action.payload;
      })
      .addCase(fetchInventorySerialData.rejected, (state, action) => {
        state.inventoryLoading = false;
        state.inventoryError = action.payload;
      })
      .addCase(deleteInventory.fulfilled, (state, action) => {
        const newData = state.data.filter(s => s.id !== action.payload);
        state.data = newData;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(
    addInventory.rejected,
    editInventory.rejected,
    fetchInventorySerialData.rejected,
    quarantineInventory.rejected,
    releaseInventory.rejected,
  ),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addInventory.fulfilled, editInventory.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(INVENTORY_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Inventory saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(quarantineInventory.fulfilled, releaseInventory.fulfilled),
  effect: (action, listenerApi) => {
    selectStatusLoading(false);
    listenerApi.dispatch(
      showToast({
        message: 'Inventory saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addInventory.fulfilled, editInventory.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(INVENTORY_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Inventory saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(fetchInventorySerialData.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(INVENTORY_ADD_EDIT, action.payload));
  },
});

redux.listener.startListening({
  matcher: isAnyOf(deleteInventory.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Inventory deleted successfully',
        type: 'success',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);
